import React, { useMemo } from "react";

export interface TabItem<T> {
  value: T;
  label: React.ReactNode;
  ariaLabel?: string;
  disabled?: boolean;
}

export interface TabSwitchProps<T> {
  items: TabItem<T>[];
  selected: T;
  onChange: (value: T) => void;
  ariaLabel?: string;
  className?: string; // container class
  optionClassName?: string; // per-option class
  selectedClassName?: string; // applied when selected
  getKey?: (value: T, index: number) => React.Key;
}

// Generic segmented tablist with roving tabindex and arrow-key navigation
const TabSwitch = <T,>({
  items,
  selected,
  onChange,
  ariaLabel,
  className,
  optionClassName,
  selectedClassName = "selected",
  getKey,
}: TabSwitchProps<T>) => {
  const selectedIndex = useMemo(
    () => items.findIndex((it) => Object.is(it.value, selected)),
    [items, selected]
  );

  if (selectedIndex < 0) {
    onChange(items[Math.floor(items.length / 2)]!.value);
  }

  return (
    <div role="tablist" aria-label={ariaLabel} className={className}>
      {items.map((item, index) => {
        const isSelected = selectedIndex === index;
        const tabIndex = isSelected || selectedIndex < 0 ? 0 : -1;
        const key = getKey ? getKey(item.value, index) : index;
        const classes = [optionClassName, isSelected ? selectedClassName : ""]
          .filter(Boolean)
          .join(" ");

        return (
          <button
            key={key}
            role="tab"
            type="button"
            aria-selected={isSelected}
            tabIndex={tabIndex}
            className={classes}
            onClick={() => !item.disabled && onChange(item.value)}
            disabled={item.disabled}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
};

export default TabSwitch;
